// Calendar System Types for Nexus Dashboard

// ============================================================================
// ENUMS & UNION TYPES
// ============================================================================

export type EventType = 'meeting' | 'task' | 'out-of-office' | 'focus-time'
export type EventColor = 'primary' | 'blue' | 'green' | 'orange' | 'red' | 'purple' | 'indigo' | 'emerald'
export type ResponseStatus = 'pending' | 'accepted' | 'declined' | 'tentative'
export type BookingStatus = 'confirmed' | 'cancelled' | 'completed' | 'no_show'
export type OverrideType = 'unavailable' | 'custom'
export type CalendarViewMode = 'week' | 'month' | 'day'

// ============================================================================
// CALENDAR EVENTS
// ============================================================================

export interface CalendarEvent {
  id: string
  ownerId: string
  title: string
  description?: string
  location?: string
  startTime: string // ISO timestamp
  endTime: string // ISO timestamp
  allDay: boolean
  timezone: string
  hasVideoConference: boolean
  meetingRoomId?: string
  meetingRoomLink?: string
  eventType: EventType
  color: EventColor
  isPrivate: boolean
  bookingId?: string
  createdAt: string
  updatedAt: string
  // Populated relations
  attendees?: EventAttendee[]
  owner?: {
    id: string
    email: string
    firstName?: string
    lastName?: string
    avatarUrl?: string
  }
}

export interface EventAttendee {
  id: string
  eventId: string
  adminUserId?: string
  externalEmail?: string
  externalName?: string
  responseStatus: ResponseStatus
  isOrganizer: boolean
  isOptional: boolean
  inviteSentAt?: string
  reminderSentAt?: string
  createdAt: string
  updatedAt: string
  // Populated user info (when admin_user_id is set)
  user?: {
    id: string
    email: string
    firstName?: string
    lastName?: string
    avatarUrl?: string
  }
}

// ============================================================================
// AVAILABILITY
// ============================================================================

export interface TeamAvailability {
  id: string
  adminUserId: string
  dayOfWeek: number // 0=Sunday, 1=Monday, ..., 6=Saturday
  startTime: string // HH:MM format (e.g., '09:00')
  endTime: string // HH:MM format (e.g., '17:00')
  isAvailable: boolean
  timezone: string
  createdAt: string
  updatedAt: string
}

export interface AvailabilityOverride {
  id: string
  adminUserId: string
  overrideDate: string // YYYY-MM-DD format
  overrideType: OverrideType
  startTime?: string // HH:MM format (for 'custom' type)
  endTime?: string // HH:MM format (for 'custom' type)
  reason?: string
  createdAt: string
  updatedAt: string
}

// Computed availability for a specific day
export interface DayAvailability {
  date: string // YYYY-MM-DD
  isAvailable: boolean
  slots: TimeSlot[]
  override?: AvailabilityOverride
}

// ============================================================================
// PUBLIC BOOKING
// ============================================================================

export interface PublicBookingPage {
  id: string
  adminUserId: string
  slug: string
  title: string
  description?: string
  avatarUrl?: string
  defaultDuration: number // minutes: 15, 30, 45, 60
  bufferBefore: number // minutes
  bufferAfter: number // minutes
  minNoticeHours: number
  maxDaysAhead: number
  allowVideoConference: boolean
  requireVideoConference: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
  // Populated user info
  user?: {
    id: string
    email: string
    firstName?: string
    lastName?: string
    avatarUrl?: string
  }
}

export interface PublicBooking {
  id: string
  bookingPageId: string
  bookerEmail: string
  bookerName: string
  bookerNotes?: string
  scheduledStart: string // ISO timestamp
  scheduledEnd: string // ISO timestamp
  durationMinutes: number
  meetingRoomId?: string
  meetingRoomLink?: string
  status: BookingStatus
  cancelToken: string
  cancelledAt?: string
  cancellationReason?: string
  confirmationSentAt?: string
  reminderSentAt?: string
  userAgent?: string
  ipAddress?: string
  createdAt: string
  updatedAt: string
  // Populated relations
  bookingPage?: PublicBookingPage
}

// ============================================================================
// TIME SLOTS
// ============================================================================

export interface TimeSlot {
  start: string // ISO timestamp or HH:MM
  end: string // ISO timestamp or HH:MM
  available: boolean
}

export interface AvailableSlots {
  date: string // YYYY-MM-DD
  timezone: string
  slots: TimeSlot[]
}

// ============================================================================
// INPUT TYPES (for creating/updating)
// ============================================================================

export interface CreateEventInput {
  title: string
  description?: string
  location?: string
  startTime: string // ISO timestamp
  endTime: string // ISO timestamp
  allDay?: boolean
  timezone?: string
  hasVideoConference?: boolean
  eventType?: EventType
  color?: EventColor
  isPrivate?: boolean
  attendees?: AttendeeInput[]
  sendInvites?: boolean
  sendFromAccountId?: string // Email account ID to send invites from
}

export interface UpdateEventInput extends Partial<CreateEventInput> {
  id: string
}

export interface AttendeeInput {
  adminUserId?: string
  externalEmail?: string
  externalName?: string
  isOptional?: boolean
}

export interface CreateAvailabilityInput {
  dayOfWeek: number
  startTime: string // HH:MM
  endTime: string // HH:MM
  isAvailable?: boolean
  timezone?: string
}

export interface CreateOverrideInput {
  overrideDate: string // YYYY-MM-DD
  overrideType: OverrideType
  startTime?: string // HH:MM (for 'custom')
  endTime?: string // HH:MM (for 'custom')
  reason?: string
}

export interface CreateBookingPageInput {
  slug: string
  title?: string
  description?: string
  defaultDuration?: number
  bufferBefore?: number
  bufferAfter?: number
  minNoticeHours?: number
  maxDaysAhead?: number
  allowVideoConference?: boolean
  requireVideoConference?: boolean
}

export interface CreateBookingInput {
  bookingPageId: string
  bookerEmail: string
  bookerName: string
  bookerNotes?: string
  scheduledStart: string // ISO timestamp
  scheduledEnd: string // ISO timestamp
}

// ============================================================================
// HOOK RETURN TYPES
// ============================================================================

export interface UseCalendarEventsOptions {
  startDate: string // ISO timestamp
  endDate: string // ISO timestamp
  ownerId?: string // Filter by owner
  includeAttendees?: boolean
}

export interface UseAvailableSlotsOptions {
  userId: string
  date: string // YYYY-MM-DD
  duration?: number // minutes, defaults to 30
}

// ============================================================================
// DATABASE ROW TYPES (snake_case, matching Supabase)
// ============================================================================

export interface CalendarEventRow {
  id: string
  owner_id: string
  title: string
  description: string | null
  location: string | null
  start_time: string
  end_time: string
  all_day: boolean
  timezone: string
  has_video_conference: boolean
  meeting_room_id: string | null
  meeting_room_link: string | null
  event_type: EventType
  color: EventColor
  is_private: boolean
  booking_id: string | null
  created_at: string
  updated_at: string
}

export interface CalendarEventAttendeeRow {
  id: string
  event_id: string
  admin_user_id: string | null
  external_email: string | null
  external_name: string | null
  response_status: ResponseStatus
  is_organizer: boolean
  is_optional: boolean
  invite_sent_at: string | null
  reminder_sent_at: string | null
  created_at: string
  updated_at: string
}

export interface TeamAvailabilityRow {
  id: string
  admin_user_id: string
  day_of_week: number
  start_time: string
  end_time: string
  is_available: boolean
  timezone: string
  created_at: string
  updated_at: string
}

export interface AvailabilityOverrideRow {
  id: string
  admin_user_id: string
  override_date: string
  override_type: OverrideType
  start_time: string | null
  end_time: string | null
  reason: string | null
  created_at: string
  updated_at: string
}

export interface PublicBookingPageRow {
  id: string
  admin_user_id: string
  slug: string
  title: string
  description: string | null
  avatar_url: string | null
  default_duration: number
  buffer_before: number
  buffer_after: number
  min_notice_hours: number
  max_days_ahead: number
  allow_video_conference: boolean
  require_video_conference: boolean
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface PublicBookingRow {
  id: string
  booking_page_id: string
  booker_email: string
  booker_name: string
  booker_notes: string | null
  scheduled_start: string
  scheduled_end: string
  duration_minutes: number
  meeting_room_id: string | null
  meeting_room_link: string | null
  status: BookingStatus
  cancel_token: string
  cancelled_at: string | null
  cancellation_reason: string | null
  confirmation_sent_at: string | null
  reminder_sent_at: string | null
  user_agent: string | null
  ip_address: string | null
  created_at: string
  updated_at: string
}

// ============================================================================
// UTILITY FUNCTIONS FOR TYPE CONVERSION
// ============================================================================

export function eventRowToEvent(row: CalendarEventRow, attendees?: EventAttendee[]): CalendarEvent {
  return {
    id: row.id,
    ownerId: row.owner_id,
    title: row.title,
    description: row.description ?? undefined,
    location: row.location ?? undefined,
    startTime: row.start_time,
    endTime: row.end_time,
    allDay: row.all_day,
    timezone: row.timezone,
    hasVideoConference: row.has_video_conference,
    meetingRoomId: row.meeting_room_id ?? undefined,
    meetingRoomLink: row.meeting_room_link ?? undefined,
    eventType: row.event_type,
    color: row.color,
    isPrivate: row.is_private,
    bookingId: row.booking_id ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    attendees,
  }
}

export function attendeeRowToAttendee(row: CalendarEventAttendeeRow): EventAttendee {
  return {
    id: row.id,
    eventId: row.event_id,
    adminUserId: row.admin_user_id ?? undefined,
    externalEmail: row.external_email ?? undefined,
    externalName: row.external_name ?? undefined,
    responseStatus: row.response_status,
    isOrganizer: row.is_organizer,
    isOptional: row.is_optional,
    inviteSentAt: row.invite_sent_at ?? undefined,
    reminderSentAt: row.reminder_sent_at ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

// Normalize time from DB format (HH:MM:SS) to UI format (HH:MM)
function normalizeTime(time: string): string {
  if (!time) return time
  // Handle both "HH:MM" and "HH:MM:SS" formats
  const parts = time.split(':')
  if (parts.length >= 2) {
    return `${parts[0]}:${parts[1]}`
  }
  return time
}

export function availabilityRowToAvailability(row: TeamAvailabilityRow): TeamAvailability {
  return {
    id: row.id,
    adminUserId: row.admin_user_id,
    dayOfWeek: row.day_of_week,
    startTime: normalizeTime(row.start_time),
    endTime: normalizeTime(row.end_time),
    isAvailable: row.is_available,
    timezone: row.timezone,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export function bookingPageRowToBookingPage(row: PublicBookingPageRow): PublicBookingPage {
  return {
    id: row.id,
    adminUserId: row.admin_user_id,
    slug: row.slug,
    title: row.title,
    description: row.description ?? undefined,
    avatarUrl: row.avatar_url ?? undefined,
    defaultDuration: row.default_duration,
    bufferBefore: row.buffer_before,
    bufferAfter: row.buffer_after,
    minNoticeHours: row.min_notice_hours,
    maxDaysAhead: row.max_days_ahead,
    allowVideoConference: row.allow_video_conference,
    requireVideoConference: row.require_video_conference,
    isActive: row.is_active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

export function bookingRowToBooking(row: PublicBookingRow): PublicBooking {
  return {
    id: row.id,
    bookingPageId: row.booking_page_id,
    bookerEmail: row.booker_email,
    bookerName: row.booker_name,
    bookerNotes: row.booker_notes ?? undefined,
    scheduledStart: row.scheduled_start,
    scheduledEnd: row.scheduled_end,
    durationMinutes: row.duration_minutes,
    meetingRoomId: row.meeting_room_id ?? undefined,
    meetingRoomLink: row.meeting_room_link ?? undefined,
    status: row.status,
    cancelToken: row.cancel_token,
    cancelledAt: row.cancelled_at ?? undefined,
    cancellationReason: row.cancellation_reason ?? undefined,
    confirmationSentAt: row.confirmation_sent_at ?? undefined,
    reminderSentAt: row.reminder_sent_at ?? undefined,
    userAgent: row.user_agent ?? undefined,
    ipAddress: row.ip_address ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

// ============================================================================
// COLOR MAPPING
// ============================================================================

// Using color-mix for theme-aware primary color with opacity
export const EVENT_COLORS: Record<EventColor, { bg: string; border: string; text: string }> = {
  primary: { bg: 'color-mix(in srgb, var(--primary) 25%, transparent)', border: 'var(--primary)', text: 'var(--text-main)' },
  blue: { bg: 'rgba(59, 130, 246, 0.25)', border: 'rgb(59, 130, 246)', text: 'rgb(191, 219, 254)' },
  green: { bg: 'rgba(34, 197, 94, 0.25)', border: 'rgb(34, 197, 94)', text: 'rgb(187, 247, 208)' },
  orange: { bg: 'rgba(249, 115, 22, 0.25)', border: 'rgb(249, 115, 22)', text: 'rgb(254, 215, 170)' },
  red: { bg: 'rgba(239, 68, 68, 0.25)', border: 'rgb(239, 68, 68)', text: 'rgb(254, 202, 202)' },
  purple: { bg: 'rgba(168, 85, 247, 0.25)', border: 'rgb(168, 85, 247)', text: 'rgb(233, 213, 255)' },
  indigo: { bg: 'rgba(99, 102, 241, 0.25)', border: 'rgb(99, 102, 241)', text: 'rgb(199, 210, 254)' },
  emerald: { bg: 'rgba(16, 185, 129, 0.25)', border: 'rgb(16, 185, 129)', text: 'rgb(167, 243, 208)' },
}

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  meeting: 'Meeting',
  task: 'Task',
  'out-of-office': 'Out of Office',
  'focus-time': 'Focus Time',
}

export const RESPONSE_STATUS_LABELS: Record<ResponseStatus, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  declined: 'Declined',
  tentative: 'Tentative',
}

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const
export const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const
